import { useState } from 'react';
import DashboardLayout from '../components/layouts/DashboardLayout';
import { useAuth } from '../hooks/useAuth';
import { useBuyCredits, type BuyablePack } from '../hooks/useBuyCredits';

type PlanKey = 'free' | 'starter' | 'growth' | 'pro';

interface Plan {
  key: PlanKey;
  name: string;
  price: string;
  period: string;
  credits: number;
  highlight: boolean;
  badge?: string;
  features: Feature[];
  cta: string;
}

interface Feature {
  label: string;
  value: string | boolean;
}

const plans: Plan[] = [
  {
    key: 'free',
    name: 'Free',
    price: '$0',
    period: 'forever',
    credits: 10,
    highlight: false,
    features: [
      { label: 'Credits', value: '10 credits' },
      { label: 'Email AI', value: 'Haiku' },
      { label: 'Direct send', value: false },
      { label: 'Auto follow-ups', value: false },
      { label: 'Resume attachment', value: false },
      { label: 'Verified emails only', value: true },
    ],
    cta: 'Downgrade to Free',
  },
  {
    key: 'starter',
    name: 'Starter',
    price: '$19',
    period: 'one-time',
    credits: 100,
    highlight: false,
    features: [
      { label: 'Credits', value: '100 credits' },
      { label: 'Email AI', value: 'Haiku' },
      { label: 'Direct send', value: false },
      { label: 'Auto follow-ups', value: false },
      { label: 'Resume attachment', value: false },
      { label: 'Verified emails only', value: true },
    ],
    cta: 'Buy Starter',
  },
  {
    key: 'growth',
    name: 'Growth',
    price: '$39',
    period: 'one-time',
    credits: 300,
    highlight: true,
    badge: 'Most Popular',
    features: [
      { label: 'Credits', value: '300 credits' },
      { label: 'Email AI', value: 'Sonnet' },
      { label: 'Direct send', value: true },
      { label: 'Auto follow-ups', value: true },
      { label: 'Resume attachment', value: true },
      { label: 'Verified emails only', value: true },
    ],
    cta: 'Buy Growth',
  },
  {
    key: 'pro',
    name: 'Pro',
    price: '$69',
    period: 'one-time',
    credits: 600,
    highlight: false,
    features: [
      { label: 'Credits', value: '600 credits' },
      { label: 'Email AI', value: 'Opus' },
      { label: 'Direct send', value: true },
      { label: 'Auto follow-ups', value: true },
      { label: 'Resume attachment', value: true },
      { label: 'Verified emails only', value: true },
    ],
    cta: 'Buy Pro',
  },
];

function CheckIcon() {
  return (
    <svg className="h-4 w-4 text-g flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg className="h-4 w-4 text-hint flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

const FAQ_ITEMS = [
  {
    q: 'Do credits expire?',
    a: 'No, your credits never expire. They roll over indefinitely and remain in your account balance until you use them.',
  },
  {
    q: 'What happens if an email bounces?',
    a: 'EextraMile automatically verifies deliverability before sending. If an outreach email ever bounces, your credit is immediately refunded back to your balance.',
  },
  {
    q: 'Can I top up credits anytime?',
    a: 'Yes! You can purchase Starter, Growth, or Pro credit packs whenever you need them. Purchased credits add directly to your current balance.',
  },
  {
    q: 'How does Auto Follow-up work?',
    a: 'Available on Growth and Pro plans. If a recruiter does not respond within 24–48 hours, EextraMile queues a personalized follow-up in your review queue for quick approval.',
  },
  {
    q: 'What is Opus Quality?',
    a: 'Pro plans utilize Anthropic Claude Opus model reasoning to draft hyper-tailored emails referencing the recruiter\'s exact background and company goals.',
  },
  {
    q: 'Can I attach my resume PDF?',
    a: 'Growth and Pro users can upload their resume PDF in Settings. EextraMile attaches it automatically to outreach emails so recruiters have everything in one place.',
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const { buyCredits, loading } = useBuyCredits();
  const currentPlan = (user?.plan ?? 'starter') as PlanKey;
  const [selectedMobilePlan, setSelectedMobilePlan] = useState<PlanKey>('growth');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const planOrder: PlanKey[] = ['free', 'starter', 'growth', 'pro'];
  const currentIdx = planOrder.indexOf(currentPlan);


  return (
    <DashboardLayout showBack backLabel="Back to Dashboard" backTo="/dashboard">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8 text-center sm:text-left">
          <h1
            className="text-[28px] sm:text-[34px] font-semibold text-ink leading-snug"
            style={{ fontFamily: 'Instrument Serif, serif' }}
          >
            Plans & Pricing
          </h1>
          <p className="text-sm sm:text-[15px] text-muted mt-1">
            Credits never expire. Each email sent costs 1 credit. Automatic refunds for bounced emails.
          </p>
        </div>

        {/* Current plan indicator */}
        <div className="mb-6 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-pill bg-g-light border border-g-border text-xs sm:text-sm font-semibold text-g-text">
          <span className="h-2 w-2 rounded-full bg-g animate-pulse" />
          You're on the <span className="capitalize font-bold">{currentPlan}</span> plan · {user?.credits_remaining ?? 0} credits remaining
        </div>

        {/* Mobile Plan Segmented Tabs */}
        <div className="sm:hidden mb-5 bg-card border border-border rounded-xl p-1 grid grid-cols-4 gap-1 shadow-sm">
          {(['free', 'starter', 'growth', 'pro'] as PlanKey[]).map(key => {
            const isSel = selectedMobilePlan === key;
            const isCurrent = currentPlan === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setSelectedMobilePlan(key)}
                className={`py-2 px-1 rounded-lg text-xs font-semibold text-center capitalize transition-colors relative ${
                  isSel ? 'bg-ink text-white shadow-sm' : 'text-muted hover:text-ink'
                }`}
              >
                {key}
                {isCurrent && !isSel && (
                  <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-g" />
                )}
              </button>
            );
          })}
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          {plans.map((plan, idx) => {
            const isCurrent = plan.key === currentPlan;
            const isUpgrade = idx > currentIdx;
            const isHiddenOnMobile = selectedMobilePlan !== plan.key;

            return (
              <div
                key={plan.key}
                className={`relative rounded-card border overflow-hidden transition-all shadow-sm hover:shadow-md ${
                  isHiddenOnMobile ? 'hidden sm:flex flex-col' : 'flex flex-col'
                } ${
                  plan.highlight
                    ? 'border-g ring-2 ring-g/30'
                    : isCurrent
                    ? 'border-g-border'
                    : 'border-border'
                }`}
                style={{ backgroundColor: 'var(--card)' }}
              >
                {/* Popular badge */}
                {plan.badge && (
                  <div className="absolute top-0 left-0 right-0 flex justify-center">
                    <span className="bg-g text-white text-[10px] font-bold uppercase tracking-widest px-3 py-0.5 rounded-b-md shadow-sm">
                      {plan.badge}
                    </span>
                  </div>
                )}

                <div className={`px-5 pt-6 pb-4 ${plan.badge ? 'pt-8' : ''}`}>
                  {/* Plan name + current badge */}
                  <div className="flex items-center gap-2 mb-3">
                    <p className="text-sm font-bold text-ink">{plan.name}</p>
                    {isCurrent && (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-g-light text-g-text border border-g-border">
                        Current
                      </span>
                    )}
                  </div>

                  {/* Price */}
                  <div className="flex items-baseline gap-1 mb-0.5">
                    <span
                      className="text-[34px] font-semibold text-ink leading-none"
                      style={{ fontFamily: 'Instrument Serif, serif', letterSpacing: '-1px' }}
                    >
                      {plan.price}
                    </span>
                  </div>
                  <p className="text-xs text-hint mb-5">{plan.period}</p>

                  {/* CTA */}
                  {isCurrent ? (
                    <div className="w-full py-2.5 rounded-input text-xs font-semibold text-center bg-bg border border-border text-muted cursor-default">
                      Current Plan
                    </div>
                  ) : (
                    <button
                      disabled={loading === plan.key}
                      onClick={() => {
                        if (isUpgrade && plan.key !== 'free') {
                          buyCredits(plan.key as BuyablePack);
                        }
                      }}
                      className={`w-full py-2.5 rounded-input text-xs font-semibold transition-all shadow-sm ${
                        isUpgrade
                          ? 'text-white hover:opacity-95 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed'
                          : 'text-muted border border-border hover:text-ink hover:bg-bg'
                      }`}
                      style={isUpgrade ? { backgroundColor: 'var(--g)' } : {}}
                      onMouseEnter={e => {
                        if (isUpgrade && loading !== plan.key) e.currentTarget.style.backgroundColor = 'var(--g-text)';
                      }}
                      onMouseLeave={e => {
                        if (isUpgrade) e.currentTarget.style.backgroundColor = 'var(--g)';
                      }}
                    >
                      {loading === plan.key ? 'Opening...' : plan.cta}
                    </button>
                  )}
                </div>

                {/* Divider */}
                <div className="border-t border-border mx-5" />

                {/* Features */}
                <div className="px-5 py-4 flex flex-col gap-3 flex-1">
                  {plan.features.map(f => (
                    <div key={f.label} className="flex items-center gap-2.5">
                      {typeof f.value === 'boolean' ? (
                        f.value ? <CheckIcon /> : <CrossIcon />
                      ) : (
                        <span className="h-4 w-4 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="text-xs text-hint leading-none mb-0.5">{f.label}</p>
                        {typeof f.value === 'string' && (
                          <p className="text-xs font-semibold text-ink">{f.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Minimal FAQ Accordion Section ── */}
        <div className="max-w-3xl mx-auto mt-12 mb-10">
          <div className="mb-6 text-center">
            <h2 className="text-xl sm:text-2xl font-semibold text-ink" style={{ fontFamily: 'Instrument Serif, serif' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-xs sm:text-sm text-muted mt-1">
              Everything you need to know about EextraMile AI credits and plans.
            </p>
          </div>

          <div className="divide-y divide-border border-t border-b border-border">
            {FAQ_ITEMS.map((item, idx) => {
              const isOpen = openFaqIndex === idx;
              return (
                <div key={item.q} className="transition-colors">
                  <button
                    type="button"
                    onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                    className="w-full py-4 flex items-center justify-between text-left gap-4 transition-colors group cursor-pointer focus:outline-none"
                    aria-expanded={isOpen}
                  >
                    <span
                      className={`text-sm font-semibold transition-colors duration-200 ${
                        isOpen ? 'text-g' : 'text-ink group-hover:text-g'
                      }`}
                    >
                      {item.q}
                    </span>
                    <span
                      className={`flex items-center justify-center w-5 h-5 flex-shrink-0 transition-colors duration-200 ${
                        isOpen ? 'text-g' : 'text-muted group-hover:text-ink'
                      }`}
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                          transition: 'transform 300ms cubic-bezier(0.16, 1, 0.3, 1)',
                        }}
                      >
                        <line x1="8" y1="2" x2="8" y2="14" />
                        <line x1="2" y1="8" x2="14" y2="8" />
                      </svg>
                    </span>
                  </button>
                  <div
                    className="grid overflow-hidden"
                    style={{
                      gridTemplateRows: isOpen ? '1fr' : '0fr',
                      transition: 'grid-template-rows 300ms cubic-bezier(0.16, 1, 0.3, 1)',
                    }}
                  >
                    <div className="overflow-hidden">
                      <p
                        className="pb-4 pr-6 text-xs sm:text-sm text-muted leading-relaxed"
                        style={{
                          opacity: isOpen ? 1 : 0,
                          transform: isOpen ? 'translateY(0)' : 'translateY(-4px)',
                          transition: 'opacity 250ms ease, transform 280ms cubic-bezier(0.16, 1, 0.3, 1)',
                        }}
                      >
                        {item.a}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer note */}
        <p className="text-xs text-hint text-center sm:text-left">
          All plans include unlimited job application tracking. Credits are consumed only when emails are sent.
        </p>
      </div>
    </DashboardLayout>
  );
}
